import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('d3_token'));
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        } else {
          localStorage.removeItem('d3_token');
          setToken(null);
        }
      } catch {
        localStorage.removeItem('d3_token');
        setToken(null);
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    
    // Read response body only once as text, then parse
    const responseText = await res.text();
    let data;
    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch {
      data = { detail: responseText || 'Login failed' };
    }
    
    if (!res.ok) {
      // User-friendly error messages
      const errorMessage = data.detail || data.message || 'Login failed. Please check your credentials.';
      throw new Error(errorMessage);
    }
    
    localStorage.setItem('d3_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const signup = useCallback(async (name, email, password, company, role, inviteToken, pendingPlan) => {
    const body = { name, email, password, company, role };
    if (inviteToken) body.inviteToken = inviteToken;
    if (pendingPlan) body.pendingPlan = pendingPlan;

    const res = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    
    // Read response body only once as text, then parse
    const responseText = await res.text();
    let data;
    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch {
      data = { detail: responseText || 'Signup failed' };
    }
    
    if (!res.ok) {
      // User-friendly error messages
      const errorMessage = data.detail || data.message || 'Signup failed. Please try again.';
      throw new Error(errorMessage);
    }
    
    localStorage.setItem('d3_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      }
    } catch {}
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem('d3_token');
    sessionStorage.removeItem('d3_pending_plan');
    setToken(null);
    setUser(null);
    window.history.pushState({}, '', '/');
  }, []);

  const demoLogin = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/auth/demo-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Demo login failed');
    localStorage.setItem('d3_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Request failed');
    return data;
  }, []);

  const resetPassword = useCallback(async (resetToken, newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Reset failed');
    return data;
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    billingRequired: user?.billingRequired === true,
    isDemo: user?.isDemo === true,
    login,
    signup,
    demoLogin,
    logout,
    refreshUser,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
